/** @jsx jsx */
import * as React from 'react';
import Clear from '@clearkit/icons/glyphs/Clear';
import DirectionBack from '@clearkit/icons/glyphs/DirectionBack';
import DirectionForward from '@clearkit/icons/glyphs/DirectionForward';
import Highlighter from 'react-highlight-words';
import { FixedSizeList as List } from 'react-window';
import { GetItemPropsOptions } from 'downshift';
import { jsx, css } from '@emotion/core';

import { CKAttributeSelectorItem } from '../CKAttributeSelectorItem';
import {
  defaultAccessoryRenderer as renderAdornment,
  AttributeComparator,
} from '../CKAttributeSelector/utils';
import { HierarchyAttribute } from '../../common/types';
import { ItemGroup, CKOptionGroupsMenu } from '../CKOptionGroupsMenu';
import {
  SelectorCategory,
  DisplayableAttribute,
  SelectorSubcategory,
} from '../CKAttributeSelector';
import { CKTab, CKTabList, CKTabs } from '../CKTab';

interface MenuListProps {
  maxHeight: number;
  isTypeMismatch: (attribute: HierarchyAttribute) => boolean;
  getItemProps: (options: GetItemPropsOptions<HierarchyAttribute>) => any;
  attributeRenderer: (attribute: DisplayableAttribute) => string;
  listRef?: any;
  itemsForSelection: HierarchyAttribute[];
  selectedAttribute: HierarchyAttribute | null;
  selectedCategory: SelectorCategory;
  selectedSubcategory: SelectorSubcategory | null;
  highlightedAttribute: HierarchyAttribute | null;
  query: string | undefined;
  queryResults: ItemGroup<HierarchyAttribute>[] | HierarchyAttribute[] | null;
  onCategorySelected: (Attribute: SelectorCategory) => void;
  onSubcategorySelected: (Attribute: SelectorSubcategory) => void;
  onBackNavigation: () => void;
  children?: React.ReactNode;
  attributeComparator: AttributeComparator;
}

export const CKAttributeSelectorMenu = (props: MenuListProps) => {
  const {
    maxHeight,
    isTypeMismatch,
    getItemProps,
    attributeRenderer,
    listRef,
    itemsForSelection,
    selectedAttribute,
    selectedCategory,
    selectedSubcategory,
    highlightedAttribute,
    query,
    queryResults,
    onCategorySelected,
    onSubcategorySelected,
    onBackNavigation,
    attributeComparator,
    children,
  } = props;

  return (
    <div
      className="ck-menu ck-menu--options"
      css={css`
        outline: 0;
        padding-top: 0;
        padding-bottom: 0;
        width: inherit;
      `}
    >
      {children}

      {queryResults ? (
        <CKOptionGroupsMenu
          options={queryResults}
          style={{
            height: maxHeight,
          }}
          renderItem={attribute => {
            const index = itemsForSelection.indexOf(attribute);

            const isHighlighted = highlightedAttribute === attribute;
            const isSelected = attributeComparator(
              attribute,
              selectedAttribute
            );
            const isDisabled = isTypeMismatch(attribute);
            return (
              <CKAttributeSelectorItem
                key={index}
                isHighlighted={isHighlighted}
                isSelected={isSelected}
                isDisabled={isDisabled}
                accessoryRenderer={renderAdornment(attribute, {
                  isHighlighted,
                })}
                {...getItemProps({
                  item: attribute,
                  index,
                })}
                attributeMetaRenderer={isDisabled && <Clear />}
              >
                <Highlighter
                  autoEscape
                  searchWords={[query || '']}
                  textToHighlight={attributeRenderer(attribute)}
                  highlightClassName="ck-highlight"
                ></Highlighter>
              </CKAttributeSelectorItem>
            );
          }}
        />
      ) : (
        <div className="flex flex-col min-w-full">
          <div className="bg-gray-0 border-b border-gray-200">
            {selectedCategory.label && (
              <div className="flex flex-row items-center py-3 px-4 h-11 justify-between">
                <div className="flex items-center">
                  {selectedCategory.parent && (
                    <div
                      onClick={onBackNavigation}
                      className="cursor-pointer flex justify-center items-center h-5 w-5 mr-3 rounded-full bg-gray-400"
                    >
                      <DirectionBack
                        className="text-white"
                        height={16}
                        width={16}
                      />
                    </div>
                  )}
                  <div className="mr-2">
                    {renderAdornment(selectedCategory)}
                  </div>
                  <p className="text-sm font-medium">
                    {selectedCategory.label}
                  </p>
                </div>

                {selectedCategory.meta}
              </div>
            )}

            {selectedCategory.subcategories && selectedSubcategory && (
              <CKTabs
                isSmall
                isBordered
                index={selectedCategory.subcategories.indexOf(
                  selectedSubcategory
                )}
              >
                <CKTabList className="mx-4 -mt-2">
                  {selectedCategory.subcategories.map(subcategory => (
                    <CKTab
                      key={subcategory.label}
                      label={subcategory.label}
                      isSelected={subcategory === selectedSubcategory}
                      onClick={() => {
                        onSubcategorySelected(subcategory);
                      }}
                    >
                      {subcategory.label}
                    </CKTab>
                  ))}
                </CKTabList>
              </CKTabs>
            )}
          </div>

          {(function() {
            const attributes = selectedSubcategory
              ? selectedSubcategory.children
              : (selectedCategory.children as DisplayableAttribute[]);

            return (
              <List
                className="mt-1"
                ref={listRef}
                height={maxHeight}
                itemCount={attributes.length}
                itemSize={30}
                width="100%"
              >
                {({ index, style }) => {
                  const attribute = attributes[index];

                  if (attribute.type === 'selector_category') {
                    return (
                      <div style={style} key={index}>
                        <CKAttributeSelectorItem
                          accessoryRenderer={renderAdornment(attribute)}
                          attributeMetaRenderer={<DirectionForward />}
                          onClick={() => {
                            onCategorySelected(attribute);
                          }}
                        >
                          {attributeRenderer(attribute)}
                        </CKAttributeSelectorItem>
                      </div>
                    );
                  } else {
                    const isDisabled = isTypeMismatch(attribute);
                    const isHighlighted = highlightedAttribute === attribute;
                    return (
                      <div style={style} key={attribute.value}>
                        <CKAttributeSelectorItem
                          isSelected={attributeComparator(
                            attribute,
                            selectedAttribute
                          )}
                          isDisabled={isDisabled}
                          isHighlighted={isHighlighted}
                          accessoryRenderer={renderAdornment(attribute, {
                            isHighlighted,
                          })}
                          attributeMetaRenderer={isDisabled && <Clear />}
                          {...getItemProps({
                            item: attribute,
                            index: itemsForSelection.indexOf(attribute),
                          })}
                        >
                          {attributeRenderer(attribute)}
                        </CKAttributeSelectorItem>
                      </div>
                    );
                  }
                }}
              </List>
            );
          })()}
        </div>
      )}
    </div>
  );
};
